// Surface kinematics: the single source of truth for where a walker is on the
// cube and how it moves. Positions are face-local `(face, u, v)` cell
// coordinates plus a 4-way heading; movement crosses face edges by folding the
// 3D geometry (never a hand-written remap table — those are a sign-bug farm).
//
// Pure math: no WebGL, no DOM (00-architecture.md §1).

/** Face order is canonical (00-architecture.md §7): +X, -X, +Y, -Y, +Z, -Z. */
export type FaceId = 0 | 1 | 2 | 3 | 4 | 5;
/** Face-local heading: +U, +V, -U, -V. Counter-clockwise seen from outside. */
export type Dir4 = 0 | 1 | 2 | 3;

export type Vec3 = readonly [number, number, number];

export interface SurfacePose {
  readonly face: FaceId;
  readonly u: number; // [0, N] in cell units
  readonly v: number; // [0, N]
  readonly dir: Dir4;
}

/** Cells per cube edge (00-architecture.md §7). */
export const N = 5;
/** World size of one cell; the cube spans [-0.5, 0.5]. */
const CELL = 1 / N;
const HALF = 0.5;
/** Rounding radius of the cube's outer edges, world units (§7, matches grid.ts). */
const ROUND_RADIUS = 0.1;
/** Half-size of the box the rounding is swept around: 0.5 - r = 0.4. */
const INNER = HALF - ROUND_RADIUS;

/** Sampling distance (cells) used to finite-difference the forward vector. */
const FORWARD_EPS = 0.01;

export const FACE_IDS: readonly FaceId[] = [0, 1, 2, 3, 4, 5];
export const DIRS: readonly Dir4[] = [0, 1, 2, 3];

interface FaceBasis {
  readonly normal: Vec3;
  readonly uAxis: Vec3;
  readonly vAxis: Vec3;
  /** World position of the face's cell-(0,0) corner. */
  readonly origin: Vec3;
}

// Per-face bases, chosen so uAxis x vAxis = normal (right-handed) and the six
// faces tile the cube consistently. THIS TABLE IS THE CONVENTION — everything
// else in the file is derived from it.
const AXES: readonly { normal: Vec3; uAxis: Vec3; vAxis: Vec3 }[] = [
  { normal: [1, 0, 0], uAxis: [0, 0, -1], vAxis: [0, 1, 0] }, // 0: +X
  { normal: [-1, 0, 0], uAxis: [0, 0, 1], vAxis: [0, 1, 0] }, // 1: -X
  { normal: [0, 1, 0], uAxis: [1, 0, 0], vAxis: [0, 0, -1] }, // 2: +Y
  { normal: [0, -1, 0], uAxis: [1, 0, 0], vAxis: [0, 0, 1] }, // 3: -Y
  { normal: [0, 0, 1], uAxis: [1, 0, 0], vAxis: [0, 1, 0] }, // 4: +Z
  { normal: [0, 0, -1], uAxis: [-1, 0, 0], vAxis: [0, 1, 0] }, // 5: -Z
];

/** The (0,0) corner sits half a cube away along the normal, then back down
 * both in-plane axes so u, v in [0, N] sweep the whole face. */
export const FACES: readonly FaceBasis[] = AXES.map((a) => ({
  ...a,
  origin: [
    HALF * a.normal[0] - HALF * a.uAxis[0] - HALF * a.vAxis[0],
    HALF * a.normal[1] - HALF * a.uAxis[1] - HALF * a.vAxis[1],
    HALF * a.normal[2] - HALF * a.uAxis[2] - HALF * a.vAxis[2],
  ],
}));

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** World direction of a face-local heading. */
function dirVector(face: FaceId, dir: Dir4): Vec3 {
  const f = FACES[face];
  switch (dir) {
    case 0:
      return f.uAxis;
    case 1:
      return f.vAxis;
    case 2:
      return [-f.uAxis[0], -f.uAxis[1], -f.uAxis[2]];
    case 3:
      return [-f.vAxis[0], -f.vAxis[1], -f.vAxis[2]];
  }
}

/** True when `dir` moves along u (rather than v). */
function isAlongU(dir: Dir4): boolean {
  return dir === 0 || dir === 2;
}

function sharpPoint(face: FaceId, u: number, v: number): Vec3 {
  const f = FACES[face];
  const su = u * CELL;
  const sv = v * CELL;
  return [
    f.origin[0] + su * f.uAxis[0] + sv * f.vAxis[0],
    f.origin[1] + su * f.uAxis[1] + sv * f.vAxis[1],
    f.origin[2] + su * f.uAxis[2] + sv * f.vAxis[2],
  ];
}

/** World point on the *sharp* cube surface for a pose. */
export function poseToSharpPoint(p: SurfacePose): Vec3 {
  return sharpPoint(p.face, p.u, p.v);
}

/**
 * Project a point on the sharp cube onto the rounded box (00-architecture §7):
 * `d = p - clamp(p, -inner, inner)`, `pos = clamp + r * d/|d|`, `normal = d/|d|`.
 * For any point on the cube surface at least one coordinate is +/-0.5 > inner,
 * so |d| >= r > 0 and the normalization is always defined.
 */
export function surfaceProject(sharp: Vec3): { pos: Vec3; normal: Vec3 } {
  const cx = clamp(sharp[0], -INNER, INNER);
  const cy = clamp(sharp[1], -INNER, INNER);
  const cz = clamp(sharp[2], -INNER, INNER);
  const dx = sharp[0] - cx;
  const dy = sharp[1] - cy;
  const dz = sharp[2] - cz;
  const len = Math.hypot(dx, dy, dz);
  const nx = dx / len;
  const ny = dy / len;
  const nz = dz / len;
  return {
    pos: [cx + ROUND_RADIUS * nx, cy + ROUND_RADIUS * ny, cz + ROUND_RADIUS * nz],
    normal: [nx, ny, nz],
  };
}

/**
 * Placement frame on the rounded cube. `forward` is finite-differenced from the
 * *rounded* positions rather than taken from the sharp face tangent: the sharp
 * tangent snaps by 90 degrees at an edge, while the rounded difference pitches
 * smoothly around it — which is what a walker's body must do.
 */
export function poseToWorld(p: SurfacePose): { pos: Vec3; normal: Vec3; forward: Vec3 } {
  const here = surfaceProject(poseToSharpPoint(p));
  const ahead = surfaceProject(poseToSharpPoint(advance(p, FORWARD_EPS)));
  const dx = ahead.pos[0] - here.pos[0];
  const dy = ahead.pos[1] - here.pos[1];
  const dz = ahead.pos[2] - here.pos[2];
  const len = Math.hypot(dx, dy, dz);
  // Degenerate only if the projection collapses the step, which the rounded
  // box never does; fall back to the sharp tangent rather than emit NaNs.
  const forward: Vec3 = len > 1e-12 ? [dx / len, dy / len, dz / len] : dirVector(p.face, p.dir);
  return { pos: here.pos, normal: here.normal, forward };
}

/**
 * Placement frame for a walker following a *curved* face-local path: same
 * rounded-box projection as `poseToWorld`, but the heading comes from an
 * arbitrary face-local tangent `(du, dv)` instead of a quarter-turn `dir`.
 * Used while a bug sweeps through a turn (`bugs.ts`), which always happens
 * well inside one face — so, unlike `poseToWorld`, this never crosses an edge.
 */
export function frameAtHeading(
  face: FaceId,
  u: number,
  v: number,
  du: number,
  dv: number,
): { pos: Vec3; normal: Vec3; forward: Vec3 } {
  const here = surfaceProject(sharpPoint(face, u, v));
  const ahead = surfaceProject(sharpPoint(face, u + du * FORWARD_EPS, v + dv * FORWARD_EPS));
  const dx = ahead.pos[0] - here.pos[0];
  const dy = ahead.pos[1] - here.pos[1];
  const dz = ahead.pos[2] - here.pos[2];
  const len = Math.hypot(dx, dy, dz);
  const forward: Vec3 = len > 1e-12 ? [dx / len, dy / len, dz / len] : FACES[face].uAxis;
  return { pos: here.pos, normal: here.normal, forward };
}

/** Face-local unit vector of a heading, as `(du, dv)` in cell units. */
export const DIR_UV: readonly (readonly [number, number])[] = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
];

/**
 * Distance to the next tile boundary along the pose's heading. Always > 0: a
 * walker sitting exactly on a boundary has a full cell to the next one, which
 * is also true right after an edge crossing (the entry coordinate is 0 or N).
 */
export function distanceToNextTile(p: SurfacePose): number {
  const along = isAlongU(p.dir) ? p.u : p.v;
  const frac = along - Math.floor(along);
  const forward = p.dir === 0 || p.dir === 1;
  if (frac === 0) return 1;
  return forward ? 1 - frac : frac;
}

/**
 * The solid grid cell a pose stands on: the block *behind* the surface at
 * `(face, floor(u), floor(v))`. Found by stepping from that cell's surface
 * centre half a cell inward along the face normal, then binning into the grid.
 */
export function poseToCell(p: SurfacePose): { x: number; y: number; z: number } {
  const f = FACES[p.face];
  const s = sharpPoint(p.face, Math.floor(p.u) + 0.5, Math.floor(p.v) + 0.5);
  const inward = CELL / 2;
  return {
    x: cellIndex(s[0] - f.normal[0] * inward),
    y: cellIndex(s[1] - f.normal[1] * inward),
    z: cellIndex(s[2] - f.normal[2] * inward),
  };
}

function cellIndex(world: number): number {
  return clamp(Math.floor((world + HALF) / CELL), 0, N - 1);
}

// ---------------------------------------------------------------------------
// Edge transitions
// ---------------------------------------------------------------------------

interface EdgeLink {
  readonly face: FaceId;
  readonly dir: Dir4;
  /** Transverse coordinate remap: `newT = scale * oldT + offset` (scale = +/-1). */
  readonly scale: number;
  readonly offset: number;
}

/**
 * Fold one crossing in 3D: a walker on `face` heading `dir` reaches the edge at
 * transverse coordinate `t`. The face it steps onto is the one whose normal is
 * the *heading* it arrived with, and its new heading is the old face's outward
 * normal reversed (it is now walking away from the face it came from).
 */
function foldCrossing(face: FaceId, dir: Dir4, t: number): { face: FaceId; dir: Dir4; t: number } {
  const alongU = isAlongU(dir);
  const atMax = dir === 0 || dir === 1;
  const u = alongU ? (atMax ? N : 0) : t;
  const v = alongU ? t : atMax ? N : 0;
  const p = sharpPoint(face, u, v);

  const heading = dirVector(face, dir);
  const nf = FACES[face].normal;
  const newFace = faceWithNormal(heading);
  const newDir = dirWithVector(newFace, [-nf[0], -nf[1], -nf[2]]);

  const g = FACES[newFace];
  const rx = p[0] - g.origin[0];
  const ry = p[1] - g.origin[1];
  const rz = p[2] - g.origin[2];
  const gu = (rx * g.uAxis[0] + ry * g.uAxis[1] + rz * g.uAxis[2]) / CELL;
  const gv = (rx * g.vAxis[0] + ry * g.vAxis[1] + rz * g.vAxis[2]) / CELL;
  // The entry point lies on the shared edge, so the coordinate along `newDir`
  // is 0 by construction; only the transverse one carries information.
  return { face: newFace, dir: newDir, t: isAlongU(newDir) ? gv : gu };
}

function faceWithNormal(n: Vec3): FaceId {
  for (const f of FACE_IDS) {
    const m = FACES[f].normal;
    if (m[0] * n[0] + m[1] * n[1] + m[2] * n[2] > 0.9) return f;
  }
  throw new Error(`no face with normal ${n.join(",")}`);
}

function dirWithVector(face: FaceId, w: Vec3): Dir4 {
  for (const d of DIRS) {
    const v = dirVector(face, d);
    if (v[0] * w[0] + v[1] * w[1] + v[2] * w[2] > 0.9) return d;
  }
  throw new Error(`no dir on face ${face} along ${w.join(",")}`);
}

/**
 * `ADJACENCY[face][exitDir]` — built once by folding the geometry above, so the
 * 24 entries are derived, not typed in. The transverse remap is affine with
 * scale +/-1, recovered by folding two sample coordinates and fitting.
 */
const ADJACENCY: readonly (readonly EdgeLink[])[] = FACE_IDS.map((face) =>
  DIRS.map((dir): EdgeLink => {
    const a = foldCrossing(face, dir, 0);
    const b = foldCrossing(face, dir, N);
    const scale = (b.t - a.t) / N;
    return { face: a.face, dir: a.dir, scale, offset: a.t };
  }),
);

/** Cells left on the current face before `dir` runs off its edge. */
function distanceToEdge(p: SurfacePose): number {
  switch (p.dir) {
    case 0:
      return N - p.u;
    case 1:
      return N - p.v;
    case 2:
      return p.u;
    case 3:
      return p.v;
  }
}

function stepOnFace(p: SurfacePose, d: number): SurfacePose {
  switch (p.dir) {
    case 0:
      return { ...p, u: p.u + d };
    case 1:
      return { ...p, v: p.v + d };
    case 2:
      return { ...p, u: p.u - d };
    case 3:
      return { ...p, v: p.v - d };
  }
}

/** Step onto the neighbouring face, entering at the shared edge. */
function crossEdge(p: SurfacePose): SurfacePose {
  const link = ADJACENCY[p.face][p.dir];
  const t = clamp(link.scale * (isAlongU(p.dir) ? p.v : p.u) + link.offset, 0, N);
  const dir = link.dir;
  // Entering along `dir` means the along-coordinate starts at the edge: 0 for a
  // +U/+V heading, N for a -U/-V one. Either way a full face-run lies ahead.
  if (isAlongU(dir)) {
    return { face: link.face, dir, u: dir === 0 ? 0 : N, v: t };
  }
  return { face: link.face, dir, u: t, v: dir === 1 ? 0 : N };
}

/**
 * Move `distance` cells along the pose's heading, crossing as many face edges
 * as needed. Negative distances walk backwards.
 */
export function advance(p: SurfacePose, distance: number): SurfacePose {
  if (distance < 0) return turnBack(advance(turnBack(p), -distance));

  let pose = p;
  let left = distance;
  // Every crossing leaves a full face-run (N cells) ahead, so this terminates.
  while (left > 0) {
    const run = distanceToEdge(pose);
    if (left < run) return stepOnFace(pose, left);
    left -= run;
    pose = crossEdge(stepOnFace(pose, run));
  }
  return pose;
}

// Quarter turns. +V is 90 degrees counter-clockwise from +U seen from outside
// the face (uAxis x vAxis = normal), so turning left advances the dir index.
const LEFT: readonly Dir4[] = [1, 2, 3, 0];
const RIGHT: readonly Dir4[] = [3, 0, 1, 2];
const BACK: readonly Dir4[] = [2, 3, 0, 1];

export function turnLeft(p: SurfacePose): SurfacePose {
  return { ...p, dir: LEFT[p.dir] };
}

export function turnRight(p: SurfacePose): SurfacePose {
  return { ...p, dir: RIGHT[p.dir] };
}

export function turnBack(p: SurfacePose): SurfacePose {
  return { ...p, dir: BACK[p.dir] };
}
